'use client'

import { cn } from '../lib/cn'

export interface CTAProps {
  /** Call-to-action headline */
  headline: string
  /** Optional subline above headline */
  subline?: string
  /** Button text */
  buttonText?: string
  /** Button link href */
  buttonHref?: string
  /** Button click handler */
  onButtonClick?: () => void
  /** Theme variant */
  theme?: 'dark' | 'light'
  /** Additional className */
  className?: string
}

/**
 * CTA (Call-to-Action) Section Component
 *
 * Final call-to-action section for presentations.
 * Automatically tracked with data-section-id="cta".
 *
 * @example
 * ```tsx
 * <CTA
 *   headline="Ready to get started?"
 *   subline="Let's talk"
 *   buttonText="Get in Touch"
 *   buttonHref="mailto:hello@example.com"
 * />
 * ```
 */
export function CTA({
  headline,
  subline,
  buttonText = 'Get in Touch',
  buttonHref,
  onButtonClick,
  theme = 'dark',
  className,
}: CTAProps) {
  const isDark = theme === 'dark'

  const buttonElement = buttonHref ? (
    <a
      href={buttonHref}
      className={cn(
        'inline-flex items-center px-8 py-4 rounded-full font-medium text-lg transition-all duration-300',
        isDark
          ? 'bg-white text-gray-900 hover:bg-gray-100'
          : 'bg-gray-900 text-white hover:bg-gray-800'
      )}
    >
      {buttonText}
      <ArrowIcon />
    </a>
  ) : (
    <button
      onClick={onButtonClick}
      className={cn(
        'inline-flex items-center px-8 py-4 rounded-full font-medium text-lg transition-all duration-300',
        isDark
          ? 'bg-white text-gray-900 hover:bg-gray-100'
          : 'bg-gray-900 text-white hover:bg-gray-800'
      )}
    >
      {buttonText}
      <ArrowIcon />
    </button>
  )

  return (
    <section
      data-section-id="cta"
      data-section-label="Contact"
      className={cn(
        'relative min-h-screen flex items-center justify-center overflow-hidden',
        isDark ? 'bg-gray-900 text-white' : 'bg-white text-gray-900',
        className
      )}
    >
      {/* Content */}
      <div className="relative z-10 text-center px-8 max-w-4xl mx-auto">
        {/* Subline */}
        {subline && (
          <p
            className={cn(
              'text-xs md:text-sm tracking-[0.2em] uppercase mb-6',
              isDark ? 'text-white/60' : 'text-gray-900/60'
            )}
          >
            {subline}
          </p>
        )}

        {/* Headline */}
        <h2
          className={cn(
            'text-4xl md:text-6xl lg:text-7xl font-bold mb-12',
            isDark ? 'text-white' : 'text-gray-900'
          )}
        >
          {headline}
        </h2>

        {/* CTA Button */}
        {buttonElement}
      </div>
    </section>
  )
}

function ArrowIcon() {
  return (
    <svg
      className="w-5 h-5 ml-2"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      viewBox="0 0 24 24"
    >
      <path d="M5 12h14M12 5l7 7-7 7" />
    </svg>
  )
}
